import { useCallback, useState } from 'react';
import { selectTenant, Tenant } from 'state/tenant';
import { useSelector } from 'react-redux';
import {
  selectTwoWayHybridApply,
  TwoWayHybridApply,
} from 'state/twoWayHybridApply';
import { format } from 'date-fns';

type TwoWayHybridApplyEchoHook = () => [
  (
    returnCode: string,
    isApproved: boolean,
    description: string,
  ) => Promise<void>,
  string | null,
];

export class TwoWayHybridApplyEchoError extends Error {}

const post = (url: string, params: { [key: string]: string }) => {
  const form = document.createElement('form');
  form.method = 'post';
  form.action = url;
  form.target = '_blank';

  const hiddenField = document.createElement('input');
  hiddenField.type = 'hidden';
  hiddenField.name = 'responseData';
  hiddenField.value = JSON.stringify(params);

  form.appendChild(hiddenField);
  document.body.appendChild(form);

  form.submit();

  hiddenField.remove();
  form.remove();
};

const selectPostUrl = (
  twoWayHybridApply: TwoWayHybridApply,
  tenant: Tenant,
  isApproved: boolean,
) => {
  if (!tenant.enabledFeatures.twoWayHybridApplyEcho) {
    throw new Error('tenant does not support twoWayHybridApplyEcho');
  }

  const { apprlCallBackUrl, nonApprlCallBackUrl } = twoWayHybridApply;

  let url;
  if (isApproved) {
    url =
      apprlCallBackUrl ||
      tenant.enabledFeatures.twoWayHybridApplyEcho.apprlCallBackUrl;
  } else {
    url =
      nonApprlCallBackUrl ||
      tenant.enabledFeatures.twoWayHybridApplyEcho.nonApprlCallBackUrl;
  }

  return encodeURI(url);
};

export const useTwoWayHybridApplyEcho: TwoWayHybridApplyEchoHook = () => {
  const [error, setError] = useState<string | null>(null);
  const tenant = useSelector(selectTenant);
  const twoWayHybridApply = useSelector(selectTwoWayHybridApply);
  const { echoSessionId } = twoWayHybridApply;

  const sendTwoWayHybridApplyEcho = useCallback(
    async (
      returnCode: string,
      isApproved: boolean,
      description: string,
    ): Promise<void> => {
      setError(null);

      const url = selectPostUrl(twoWayHybridApply, tenant, isApproved);
      if (echoSessionId) {
        const payload = {
          statusCode: returnCode,
          statusDescription: description,
          clientToken: echoSessionId,
          timestamp: format(new Date(), "yyyy-MM-dd'T'HH:mm'Z'"),
        };

        post(url, payload);
      } else {
        throw new Error('No echoSessionId provided');
      }
    },
    [echoSessionId, tenant, twoWayHybridApply],
  );

  return [sendTwoWayHybridApplyEcho, error];
};

export default useTwoWayHybridApplyEcho;
